import { useEffect, useRef, useState } from 'react';
import DeleteIcon from 'src/assets/all-icons/delete-icon.jsx';

import Loader from '@/shared-components/loader/Loader';

import PostVideoControls from './PostVideoControls';
import POST_VIDEO_HLS_CONFIG from './postVideoHlsConfig';
import useVideoPresentation from './useVideoPresentation';

import './BoardBodyVideo.css';

export default function InlinePostVideo({ video, previewMode, onRemove }) {
  const blockRef = useRef(null);
  const videoRef = useRef(null);
  const mediaRef = useRef(null);
  const [isControlsActive, setIsControlsActive] = useState(false);
  const [playbackFailed, setPlaybackFailed] = useState(false);
  const playbackUrl = video?.playbackUrl || video?.url || null;
  const isHlsPlayback = /\.m3u8(\?|$)/i.test(playbackUrl || '');
  const isPlayable = video?.isPlayable ?? (video?.status === 'ready' || Boolean(playbackUrl));
  const processingFailed = video?.status === 'failed';
  const canRenderPlayer = isPlayable && !processingFailed && !playbackFailed;
  const { handleLoadedMetadata, presentationStyle } = useVideoPresentation({
    containerRef: blockRef,
    playbackUrl,
  });

  const togglePlayback = () => {
    const player = videoRef.current;
    if (!player) return;

    if (player.ended) player.currentTime = 0;
    if (player.paused) player.play().catch(() => {});
    else player.pause();
  };

  const handlePlayerKeyDown = event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    togglePlayback();
  };

  const activateControls = () => {
    setIsControlsActive(true);
  };

  const handleMediaPointerEnter = event => {
    if (event.pointerType === 'mouse') activateControls();
  };

  const handleMediaPointerLeave = event => {
    if (event.pointerType === 'mouse') setIsControlsActive(false);
  };

  const handleMediaBlur = event => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsControlsActive(false);
    }
  };

  useEffect(() => {
    setPlaybackFailed(false);
    setIsControlsActive(false);
  }, [playbackUrl]);

  useEffect(() => {
    let hls;
    let cancelled = false;
    const element = videoRef.current;
    if (!playbackUrl || !element || !isHlsPlayback) return undefined;

    const supportsNativeHls = Boolean(
      element.canPlayType('application/vnd.apple.mpegurl')
      || element.canPlayType('application/x-mpegURL')
    );

    if (supportsNativeHls) {
      element.src = playbackUrl;
      element.load();
      return () => {
        element.removeAttribute('src');
        element.load();
      };
    }

    (async () => {
      const { default: Hls } = await import('hls.js/dist/hls.min.js');
      if (cancelled) return;
      if (!Hls?.isSupported()) {
        setPlaybackFailed(true);
        return;
      }

      hls = new Hls(POST_VIDEO_HLS_CONFIG);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal) setPlaybackFailed(true);
      });
      hls.loadSource(playbackUrl);
      hls.attachMedia(element);
    })().catch(() => {
      if (!cancelled) setPlaybackFailed(true);
    });

    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [isHlsPlayback, playbackUrl]);

  return (
    <div ref={blockRef} className="post-video-block">
      {canRenderPlayer ? (
        <div
          ref={mediaRef}
          className={`post-video-block__media post-video-block__media--player${
            presentationStyle ? ' post-video-block__media--sized' : ''
          }`}
          style={presentationStyle}
          data-controls-active={isControlsActive || undefined}
          onBlurCapture={handleMediaBlur}
          onFocusCapture={activateControls}
          onPointerDown={activateControls}
          onPointerEnter={handleMediaPointerEnter}
          onPointerLeave={handleMediaPointerLeave}
        >
          <video
            ref={videoRef}
            className="post-video-block__player"
            src={isHlsPlayback ? undefined : playbackUrl}
            poster={video?.thumbnailUrl || undefined}
            playsInline
            preload="metadata"
            tabIndex={0}
            onClick={togglePlayback}
            onKeyDown={handlePlayerKeyDown}
            onLoadedMetadata={handleLoadedMetadata}
            onError={() => setPlaybackFailed(true)}
          />
          <PostVideoControls
            videoRef={videoRef}
            mediaRef={mediaRef}
            onTogglePlayback={togglePlayback}
          />
          {!previewMode && onRemove && (
            <DeleteIcon
              className="post-video-block__remove-button"
              onClick={onRemove}
            />
          )}
        </div>
      ) : playbackFailed ? (
        <div className="post-video-block__media">
          <div className="post-video-block__error" role="alert">
            This video could not be played. Please refresh and try again.
          </div>
        </div>
      ) : processingFailed ? (
        <div className="post-video-block__media">
          <div className="post-video-block__error" role="alert">
            Video processing failed. Remove it and try again.
          </div>
        </div>
      ) : (
        <div className="post-video-block__media">
          <div className="post-video-block__loading" role="status" aria-label="Video is loading">
            <Loader inline thin />
          </div>
        </div>
      )}
      {!canRenderPlayer && !previewMode && onRemove && (
        <DeleteIcon
          className="post-video-block__remove-button"
          onClick={onRemove}
        />
      )}
    </div>
  );
}
